import { composeRefs } from '@volvo-cars/react-utils';
import type React from 'react';
import { forwardRef } from 'react';
import type { BaseSelectableCardProps } from './selectable-card-base';
import { SelectableCardBase } from './selectable-card-base';

interface BaseCheckboxCardProps extends BaseSelectableCardProps {
  /**
   * The name of the input to use when submitting the form.
   */
  name?: string;

  /**
   * The value of the checkbox that will be submitted with the form data.
   *
   * @default on
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#value
   */
  value?: string;

  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the input is required.
   * @default false
   */
  required?: boolean;

  /**
   * Fires if the input fails validation on form submit.
   */
  onInvalid?: React.FormEventHandler<HTMLInputElement>;

  /**
   * Focus event handler.
   */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * Blur event handler.
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * ARIA label for the input.
   */
  'aria-label'?: string;

  /**
   * ARIA describedby for the input.
   */
  'aria-describedby'?: string;

  /**
   * ARIA invalid state.
   */
  'aria-invalid'?: boolean;
}

type ControlledProps = {
  /**
   * Whether the checkbox is checked.
   * Makes the input controlled.
   */
  checked: boolean;

  /**
   * Fires when the input is checked or unchecked.
   */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  defaultChecked?: never;
};

type UncontrolledProps = {
  /**
   * Whether an (uncontrolled) checkbox is checked by default.
   */
  defaultChecked?: boolean;

  /**
   * Fires when the input is checked or unchecked.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  checked?: never;
};

export type CheckboxCardProps = BaseCheckboxCardProps &
  (ControlledProps | UncontrolledProps);

export const CheckboxCard = forwardRef<HTMLInputElement, CheckboxCardProps>(
  function CheckboxCard(
    {
      name,
      value,
      checked,
      defaultChecked,
      onChange,
      disabled,
      required,
      title,
      meta,
      description,
      image,
      children,
      id,
      className,
      hidden,
      dir,
      lang,
      translate,
      slot,
      style,
      onInvalid,
      onFocus,
      onBlur,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      'aria-invalid': ariaInvalid,
      ...props
    }: CheckboxCardProps,
    ref: React.Ref<HTMLInputElement>,
  ) {
    return (
      <SelectableCardBase
        title={title}
        meta={meta}
        description={description}
        image={image}
        id={id}
        className={className}
        hidden={hidden}
        dir={dir}
        lang={lang}
        translate={translate}
        slot={slot}
        style={style}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        renderInput={({
          inputId,
          effectiveAriaLabelledby,
          effectiveAriaDescribedby,
          inputRef,
        }) => (
          <input
            {...props}
            ref={composeRefs(inputRef, ref)}
            type="checkbox"
            id={inputId}
            name={name}
            value={value}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={onChange}
            disabled={disabled}
            required={required}
            onInvalid={onInvalid}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-label={ariaLabel}
            aria-labelledby={effectiveAriaLabelledby}
            aria-describedby={effectiveAriaDescribedby}
            aria-invalid={ariaInvalid}
          />
        )}
      >
        {children}
      </SelectableCardBase>
    );
  },
);
